<template>
    <Head v-if="updating === false" title="Create New Organisation"/>
    <Head v-else :title="form.organisation.name"/>
    <ValidationErrors :show="showErrors"/>
    <div class="w-full space-y-3 mb-6">
        <div>
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
        <span v-if="form.organisation.name === null"
              v-html="'Create New ' + organisation.type.charAt(0).toUpperCase() + organisation.type.substring(1)"></span>
                <span v-else v-html="form.organisation.name"></span>
            </h1>
            <h2 v-if="form.organisation.registered_legal_name === null"
                class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="''"></h2>
            <h2 v-else class="mb-2 mt-0 text-md font-medium leading-7 text-gray-500 sm:truncate"
                v-html="form.organisation.registered_legal_name"></h2>
        </div>
        <div class="space-y-6 mt-2 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div v-if="updating === true" class="py-3 flex justify-between">
                        <Link v-if="updating" :href="route('frontend.organisations.suppliers.index', {_query: {organisation_id: organisation.id, open_contacts_slideout: true}})"
                              class="inline-flex h-10 items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                            <users-icon class="-ml-0.5 mr-2 h-4 w-4"></users-icon>
                            Manage Contacts
                        </Link>
                        <div class="py-3 flex justify-end">
                            <a href="#" onclick="history.back();return false;"
                                class="inline-flex h-10 items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md text-sm text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                Back
                            </a>
                            <button type="submit"
                                :class="{'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                :disabled="this.$page.props.auth.user_role == 3"
                                class="ml-5 bg-pitch-purple border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                <span v-if="updating === true">Update</span>
                                <span v-else>Save</span>
                            </button>
                        </div>
                    </div>

                    <SupplierTabs v-if="organisation.name" :organisation="organisation"/>

                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <!-- Supplier Form Start -->
                            <div id="supplier_form" class="space-y-6" v-if="form.organisation.type === 'supplier'">
                                <div class="relative">
                                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div class="w-full border-t border-gray-300"/>
                                    </div>
                                    <div class="relative flex items-center justify-between">
                                        <span class="pr-3 bg-white text-lg font-bold text-gray-900"> Service Information </span>
                                    </div>
                                </div>
                                <div>
                                    <p class="-mt-6 text-sm text-gray-500">
                                        Enter the service information for the supplier
                                        below. The information entered here will be used for creating services.
                                    </p>
                                    <div class="rounded-md bg-blue-50 p-4 mt-2">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <InformationCircleIcon class="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-blue-700">
                                                    The service type, country and region fields are used to filter 
                                                    services when adding them to a quote within a fixture.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_type" class="block text-sm font-medium text-gray-700 required">
                                            Service Type
                                        </label>
                                        <v-select class="h-full mt-1"
                                            label="name"
                                            :options="service_types"
                                            :class="{'border-red-500': form.errors['service.service_type_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :reduce="service_type => service_type.id"
                                            v-model="form.service.service_type_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-name" class="block text-sm font-medium text-gray-700 required">
                                            Name
                                        </label>
                                        <input type="text"
                                            name="service-name"
                                            v-model="form.service.name"
                                            id="service-name"
                                            autocomplete="name"
                                            :class="{'border-red-500': form.errors['service.name'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <template v-if="form.service.service_type_id == 5">
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-name" class="block text-sm font-medium text-gray-700">
                                                Supplier Chain
                                            </label>
                                            <v-select
                                                class="h-full mt-1"
                                                :class="{'border-red-500': form.errors['service.supplier_group_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                                :disabled="this.$page.props.auth.user_role == 3"
                                                :options="supplier_groups"
                                                label="name"
                                                :reduce="supplier_group => supplier_group.id"
                                                v-model="form.service.supplier_group_id"
                                            />
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-name" class="block text-sm font-medium text-gray-700">
                                                Commission Rate (%)
                                            </label>
                                            <input type="number" step=".01"
                                                id="service-commission-rate"
                                                name="service-commission-rate-percent"
                                                v-model="form.service.commission_rate_percent"
                                                autocomplete="name"
                                                :class="{'border-red-500': form.errors['service.commission_rate'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                                :disabled="this.$page.props.auth.user_role == 3"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                        </div>
                                    </template>
                                    
                                    <template v-else>
                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                            <label for="service-name" class="block text-sm font-medium text-gray-700">
                                                Commission Rate (%)
                                            </label>
                                            <input type="number" step=".01" 
                                                id="service-commission-rate"
                                                name="service-commission-rate-percent"
                                                autocomplete="name"
                                                v-model="form.service.commission_rate_percent"
                                                :class="{'border-red-500': form.errors['service.commission_rate'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                                :disabled="this.$page.props.auth.user_role == 3"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                        </div>

                                        <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>
                                    </template>
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-telephone" class="block text-sm font-medium text-gray-700">
                                            Telephone
                                        </label>
                                        <input type="text"
                                            name="service-telephone"
                                            v-model="form.service.telephone"
                                            id="service-telephone" autocomplete="telephone"
                                            :class="{'border-red-500': form.errors['service.telephone'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-mobile" class="block text-sm font-medium text-gray-700">
                                            Mobile
                                        </label>
                                        <input v-model="form.service.mobile"
                                            type="text"
                                            name="service-mobile"
                                            id="service-mobile"
                                            autocomplete="mobile"
                                            :class="{'border-red-500': form.errors['service.mobile'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-email" class="block text-sm font-medium text-gray-700">
                                            Email
                                        </label>
                                        <input v-model="form.service.email"
                                            type="text"
                                            name="service-email"
                                            id="service-email" autocomplete="email"
                                            :class="{'border-red-500': form.errors['service.email'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-website" class="block text-sm font-medium text-gray-700">
                                            Website
                                        </label>
                                        <input type="text"
                                            name="service-website"
                                            v-model="form.service.website" 
                                            id="service-website"
                                            autocomplete="website"
                                            :class="{'border-red-500': form.errors['organisation.website'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                </div>

                                <div>
                                    <h2 id="service-address-heading" class="text-lg leading-6 font-medium text-gray-900">
                                        Address
                                    </h2>
                                    <p class="mt-1 text-sm text-gray-500">
                                        Enter the service address for the service. The information entered here will be used for reference only.
                                    </p>
                                </div>

                                <div class="pt-6 grid grid-cols-4 gap-6" hidden>
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_street" class="block text-sm font-medium text-gray-700">
                                            Street
                                        </label>
                                        <input v-model="form.service_address.street"
                                            type="text"
                                            name="service-address-street" id="service-address-street"
                                            autocomplete="service_street"
                                            :class="{'border-red-500': form.errors['service_address.street'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_address_line_1" class="block text-sm font-medium text-gray-700">
                                            Address Line 1
                                        </label>
                                        <input v-model="form.service_address.address_line_1"
                                            type="text"
                                            name="service-address-address-line-1" id="service-address-address-line-1"
                                            autocomplete="address_line_1"
                                            :class="{'border-red-500': form.errors['service_address.address_line_1'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_address_line_2" class="block text-sm font-medium text-gray-700">
                                            Address Line 2
                                        </label>
                                        <input v-model="form.service_address.address_line_2"
                                            type="text"
                                            name="service-address-address-line-2" id="service-address-address-line-2"
                                            autocomplete="address_line_2"
                                            :class="{'border-red-500': form.errors['service_address.address_line_2'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_address_line_3" class="block text-sm font-medium text-gray-700">
                                            Address Line 3
                                        </label>
                                        <input v-model="form.service_address.address_line_3"
                                            type="text"
                                            name="service-address-address-line-3" id="service-address-address-line-3"
                                            autocomplete="address_line_3"
                                            :class="{'border-red-500': form.errors['service_address.address_line_3'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_city" class="block text-sm font-medium text-gray-700">
                                            City
                                        </label>
                                        <input v-model="form.service_address.city"
                                            type="text"
                                            name="service-address-city" id="service-address-city" autocomplete="city"
                                            :class="{'border-red-500': form.errors['service_address.city'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service_code" class="block text-sm font-medium text-gray-700">
                                            Postal / Zip Code
                                        </label>
                                        <input v-model="form.service_address.code"
                                            type="text"
                                            name="service-address-code" id="service-address-code" autocomplete="code"
                                            :class="{'border-red-500': form.errors['service_address.code'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-address-country" class="block text-sm font-medium text-gray-700 required">
                                            Country
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['service_address.country_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :options="countries"
                                            label="name"
                                            :reduce="countries => countries.id"
                                            v-model="form.service_address.country_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="service-address-region" class="block text-sm font-medium text-gray-700 required">
                                            Locations / Venue
                                        </label>
                                        <v-select class="mt-1"
                                            :class="{'border-red-500': form.errors.region_ids, 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="form.service_address.country_id < 1 || this.$page.props.auth.user_role == 3"
                                            :options="regions" label="name" :reduce="(region) => region.id"
                                            :create-option="region => ({ name: region.name })"
                                            v-model="form.region_ids" taggable multiple/>
                                    </div>

                                    <div class="col-span-4 mb-[70px] clear-both">
                                        <label for="service-notes" class="block text-sm font-medium text-gray-700">
                                            Notes
                                        </label>
                                        <quill-editor content-type="html"
                                            v-model:content="form.service.notes"
                                            :class="{'border-red-500': form.errors['service.notes'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                            theme="snow"></quill-editor>
                                    </div>

                                    <div class="col-span-4 mb-[70px] clear-both">
                                        <label for="service-commission-notes" class="block text-sm font-medium text-gray-700">
                                            Commission Notes
                                        </label>
                                        <textarea
                                            v-model="form.service.commission_notes"
                                            :class="{'border-red-500': form.errors['service.commission_notes'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                            theme="snow"></textarea>
                                    </div>
                                </div>
                            </div>

                            <!-- Organisation Form Start -->
                            <div id="organisation_form" class="space-y-6">
                                <div class="border-b border-gray-200 pb-2">
                                    <div class="relative">
                                        <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                            <div class="w-full border-t border-gray-300"/>
                                        </div>
                                        <div class="relative flex items-center justify-between">
                                            <span class="pr-3 bg-white text-lg font-bold text-gray-900">
                                                Organisation Information
                                            </span>
                                            <button @click="copyToOrganisation"
                                                type="button"
                                                v-if="form.organisation.type === 'supplier'"
                                                class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <ClipboardCopyIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400" aria-hidden="true"/>
                                                <span>
                                                    Copy Service Information
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="flex justify-between items-center">
                                        <p class="mt-1 text-sm text-gray-500 w-1/2">
                                            Enter information for the organisation below.
                                        </p>
                                        <p class="mt-1 text-right text-xs text-gray-500 font-italic w-1/2">
                                            Fields marked with an asterisk (<span class="text-red-500">*</span>) are required.
                                        </p>
                                    </div>
                                </div>

                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="name" class="block text-sm font-medium text-gray-700 required">
                                            Name
                                        </label>
                                        <input type="text"
                                            v-model="form.organisation.name"
                                            name="name"
                                            id="name"
                                            autocomplete="name"
                                            :class="{'border-red-500': form.errors['organisation.name'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1"
                                         v-if="form.organisation.type === 'client'">
                                        <label for="short_code"
                                            class="block text-sm font-medium text-gray-700"
                                            :class="{'required' : form.organisation.type === 'client' || form.organisation.type === ''}">
                                            Short Code
                                        </label>
                                        <input type="text" v-model="form.organisation.short_code"
                                            name="short_code" id="short_code"
                                            autocomplete="short_code"
                                            maxlength="4"
                                            :class="{'border-red-500': form.errors['organisation.short_code'], 'disabled': (this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4) || (this.$page.props.auth.user_role == 3)}"
                                            :disabled="(this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4) || (this.$page.props.auth.user_role == 3)"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-gray-100" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="registered_legal_name" class="block text-sm font-medium text-gray-700 required">
                                            Registered Legal Name
                                        </label>
                                        <input type="text"
                                            v-model="form.organisation.registered_legal_name"
                                            name="registered_legal_name" id="registered_legal_name"
                                            autocomplete="registered_legal_name"
                                            :class="{'border-red-500': form.errors['organisation.registered_legal_name'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="trading_details_id" class="block text-sm font-medium text-gray-700 required">
                                            Trading Name
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['organisation.trading_details_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3 || updating === true"
                                            :options="trading_details" label="trading_name" :reduce="trading_details => trading_details.id"
                                            v-model="form.organisation.trading_details_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="trading_details_id" class="block text-sm font-medium text-gray-700">
                                            TTM
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['organisation.ttm_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :options="users" label="name" :reduce="users => users.id"
                                            v-model="form.organisation.ttm_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1 hidden">
                                        <label for="organisation_type" class="block text-sm font-medium text-gray-700 required">
                                            Organisation Type
                                        </label> 
                                        <select id="organisation_type" v-model="form.organisation.type"
                                            :class="{'border-red-500': form.errors['organisation.type'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select an organisation type'"></option>
                                            <option v-for="type in organisation_types" :value="type" v-html="type.charAt(0).toUpperCase() + type.substring(1)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="organisation-payment_terms" class="block text-sm font-medium text-gray-700 required">
                                            Payment Terms
                                        </label>
                                        <input v-model="form.organisation.payment_terms"
                                            type="number"
                                            name="organisation-payment_terms" id="organisation-payment_terms"
                                            autocomplete="telephone"
                                            :class="{'border-red-500': form.errors['organisation.payment_terms'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="organisation-currency" class="block text-sm font-medium text-gray-700 required">
                                            Invoice Currency
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['organisation.invoice_currency_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :options="currencies"
                                            label="name"
                                            :reduce="currencies => currencies.id"
                                            v-model="form.organisation.invoice_currency_id" />
                                    </div>

                                    <div v-if="form.organisation.type !== 'client'" class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="commission-vat-type" class="block text-sm font-medium text-gray-700 required">
                                            Commission VAT Type
                                        </label>
                                        <select v-model="form.organisation.commission_vat_type"
                                            id="commission-vat-type"
                                            :class="{'border-red-500': form.errors['organisation.commission_vat_type'], 'disabled': this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4, 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value="net">Commission does not include VAT</option>
                                            <option value="gross">Commission value includes VAT </option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="organisation-cmid" class="block text-sm font-medium text-gray-700">
                                            CMID
                                        </label>
                                        <input type="text"
                                            v-model="form.organisation.cmid"
                                            name="cmid"
                                            id="cmid"
                                            autocomplete="cmid"
                                            :class="{'border-red-500': form.errors['organisation.cmid'], 'disabled': this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4, 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="(this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4) || (this.$page.props.auth.user_role == 3)"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-gray-100"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1" v-if="organisation.type == 'supplier'">
                                        <label for="organisation-smid" class="block text-sm font-medium text-gray-700">
                                            SMID
                                        </label>
                                        <input type="text"
                                            v-model="form.organisation.smid"
                                            name="smid"
                                            id="smid"
                                            autocomplete="smid"
                                            :class="{'border-red-500': form.errors['organisation.cmid'], 'disabled': this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4, 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="(this.$page.props.auth.user_role != 1 && this.$page.props.auth.user_role != 4) || (this.$page.props.auth.user_role == 3)"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm disabled:bg-gray-100"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1"></div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="telephone" class="block text-sm font-medium text-gray-700 required">
                                            Telephone
                                        </label>
                                        <input v-model="form.organisation.telephone"
                                            type="text"
                                            name="telephone"
                                            id="telephone" autocomplete="telephone"
                                            :class="{'border-red-500': form.errors['organisation.telephone'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="mobile" class="block text-sm font-medium text-gray-700">
                                            Mobile
                                        </label>
                                        <input v-model="form.organisation.mobile"
                                            type="text"
                                            name="mobile"
                                            id="mobile"
                                            autocomplete="mobile"
                                            :class="{'border-red-500': form.errors['organisation.mobile'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="email" class="block text-sm font-medium text-gray-700 required">
                                            Email
                                        </label>
                                        <input v-model="form.organisation.email"
                                            type="text"
                                            name="email"
                                            id="email"
                                            autocomplete="email"
                                            :class="{'border-red-500': form.errors['organisation.email'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="website" class="block text-sm font-medium text-gray-700">
                                            Website
                                        </label>
                                        <input v-model="form.organisation.website"
                                            type="text" 
                                            name="website"
                                            id="website"
                                            autocomplete="website"
                                            :class="{'border-red-500': form.errors['organisation.website'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                </div>

                                <div>
                                    <h2 id="organisation-address-heading" class="text-lg leading-6 font-medium text-gray-900">
                                        Address
                                    </h2>
                                    <p class="mt-1 text-sm text-gray-500">
                                        Enter the address for the organisation below. The address entered here is used for reference.
                                    </p>
                                </div>

                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="street" class="block text-sm font-medium text-gray-700">
                                            Street
                                        </label>
                                        <input v-model="form.organisation_address.street"
                                            type="text"
                                            name="street"
                                            id="street" autocomplete="street"
                                            :class="{'border-red-500': form.errors['organisation_address.street'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="address_line_1" class="block text-sm font-medium text-gray-700">
                                            Address Line 1
                                        </label>
                                        <input v-model="form.organisation_address.address_line_1"
                                            type="text"
                                            name="address_line_1" id="address_line_1"
                                            autocomplete="address_line_1"
                                            :class="{'border-red-500': form.errors['organisation_address.address_line_1'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="address_line_2" class="block text-sm font-medium text-gray-700">
                                            Address Line 2
                                        </label>
                                        <input v-model="form.organisation_address.address_line_2"
                                            type="text"
                                            name="address_line_2" id="address_line_2"
                                            autocomplete="address_line_2"
                                            :class="{'border-red-500': form.errors['organisation_address.address_line_2'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="address_line_3" class="block text-sm font-medium text-gray-700">
                                            Address Line 3
                                        </label>
                                        <input v-model="form.organisation_address.address_line_3"
                                            type="text"
                                            name="address_line_3" id="address_line_3"
                                            autocomplete="address_line_3"
                                            :class="{'border-red-500': form.errors['organisation_address.address_line_3'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="city" class="block text-sm font-medium text-gray-700">
                                            City
                                        </label>
                                        <input v-model="form.organisation_address.city"
                                            type="text"
                                            name="city"
                                            id="city"
                                            autocomplete="city"
                                            :class="{'border-red-500': form.errors['organisation_address.city'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="state" class="block text-sm font-medium text-gray-700">
                                            State
                                        </label>
                                        <input v-model="form.organisation_address.state"
                                            type="text"
                                            name="state"
                                            id="state"
                                            autocomplete="state"
                                            :class="{'border-red-500': form.errors['organisation_address.state'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="code" class="block text-sm font-medium text-gray-700">
                                            Postal / Zip Code
                                        </label>
                                        <input v-model="form.organisation_address.code"
                                            type="text"
                                            name="code"
                                            id="code"
                                            autocomplete="code"
                                            :class="{'border-red-500': form.errors['organisation_address.code'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="email" class="block text-sm font-medium text-gray-700">
                                            Country
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['organisation_address.country_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :options="countries"
                                            label="name"
                                            :reduce="countries => countries.id"
                                            v-model="form.organisation_address.country_id" />
                                    </div>
                                </div>
                            </div>

                            <!-- Tax Information Form Start -->
                            <div id="tax_form" class="space-y-6">
                                <div class="relative">
                                    <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div class="w-full border-t border-gray-300"/>
                                    </div>
                                    <div class="relative flex items-center justify-between">
                                        <span class="pr-3 bg-white text-lg font-bold text-gray-900">
                                            Tax & Billing Details
                                        </span>
                                        <div class="flex items-center gap-2">
                                            <button @click="copyToTax"
                                                type="button"
                                                class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <ClipboardCopyIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400" aria-hidden="true"/>
                                                <span>
                                                    Copy Organisation Information
                                                </span>
                                            </button>
                                            <button @click="copyServiceToTax" type="button" class="inline-flex items-center shadow-sm px-4 py-1.5 border border-gray-300 text-sm leading-5 font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                <ClipboardCopyIcon class="-ml-1.5 mr-1 h-5 w-5 text-gray-400" aria-hidden="true"/>
                                                <span>
                                                    Copy Service Information
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p class="-mt-6 text-sm text-gray-500">
                                        Enter the tax and billing details for this organisation.
                                    </p>
                                    <div class="rounded-md bg-blue-50 p-4 mt-2">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <InformationCircleIcon class="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-blue-700">
                                                    Details entered here will appear on organisation invoices.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-company-name" class="block text-sm font-medium text-gray-700 required">
                                            Company Name
                                        </label>
                                        <input type="text"
                                            v-model="form.tax.company_name"
                                            name="tax-company-name"
                                            id="tax-company-name"
                                            autocomplete="name"
                                            :class="{'border-red-500': form.errors['tax.company_name'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-vat-id" class="block text-sm font-medium text-gray-700">
                                            VAT ID
                                        </label>
                                        <input type="text"
                                            v-model="form.tax.vat_id"
                                            name="tax-vat-id"
                                            id="tax-vat-id"
                                            autocomplete="name"
                                            :class="{'border-red-500': form.errors['tax.vat_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                </div>

                                <div>
                                    <h2 id="tax-address-heading" class="text-lg leading-6 font-medium text-gray-900">Address</h2>
                                </div>

                                <div class="pt-6 grid grid-cols-4 gap-6" hidden>
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-street" class="block text-sm font-medium text-gray-700 required">
                                            Street
                                        </label>
                                        <input v-model="form.tax_address.street"
                                            type="text"
                                            name="tax-address-street"
                                            id="tax-address-street"
                                            autocomplete="tax-address-street"
                                            :class="{'border-red-500': form.errors['tax_address.street'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-address-line-1" class="block text-sm font-medium text-gray-700">
                                            Address Line 1
                                        </label>
                                        <input v-model="form.tax_address.address_line_1"
                                            type="text"
                                            name="tax-address-address-line-1" id="tax-address-address-line-1"
                                            autocomplete="address-line-1"
                                            :class="{'border-red-500': form.errors['tax_address.address_line_1'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-address-line-2" class="block text-sm font-medium text-gray-700">
                                            Address Line 2
                                        </label>
                                        <input v-model="form.tax_address.address_line_2"
                                            type="text"
                                            name="tax-address-address-line-2" id="tax-address-address-line-2"
                                            autocomplete="address-line-2"
                                            :class="{'border-red-500': form.errors['tax_address.address_line_2'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-address-line-3" class="block text-sm font-medium text-gray-700">
                                            Address Line 3
                                        </label>
                                        <input v-model="form.tax_address.address_line_3"
                                            type="text"
                                            name="tax-address-address-line-3" id="tax-address-address-line-3"
                                            autocomplete="address-line-3"
                                            :class="{'border-red-500': form.errors['tax_address.address_line_3'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-city" class="block text-sm font-medium text-gray-700 required">
                                            City
                                        </label>
                                        <input v-model="form.tax_address.city"
                                            type="text"
                                            name="tax-address-city"
                                            id="tax-address-city" autocomplete="city"
                                            :class="{'border-red-500': form.errors['tax_address.city'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="state" class="block text-sm font-medium text-gray-700">
                                            State
                                        </label>
                                        <input v-model="form.tax_address.state"
                                            type="text"
                                            name="state"
                                            id="state"
                                            autocomplete="state"
                                            :class="{'border-red-500': form.errors['tax_address.state'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-code" class="block text-sm font-medium text-gray-700">
                                            Postal / Zip Code
                                        </label>
                                        <input v-model="form.tax_address.code"
                                            type="text"
                                            name="tax-address-code"
                                            id="tax-address-code" autocomplete="code"
                                            :class="{'border-red-500': form.errors['tax_address.code'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="tax-address-country" class="block text-sm font-medium text-gray-700 required">
                                            Country
                                        </label>
                                        <v-select class="h-full mt-1"
                                            :class="{'border-red-500': form.errors['tax_address.country_id'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                            :disabled="this.$page.props.auth.user_role == 3"
                                            :options="countries"
                                            label="name" :reduce="countries => countries.id"
                                            v-model="form.tax_address.country_id" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-2">
                                        <div class="flex items-center">
                                            <label for="management_fee_separate" class="block text-sm font-medium text-gray-700">
                                                Monthly Management Fee Billing
                                            </label>
                                            <div class="ml-3 flex">
                                                <input type="checkbox"
                                                    id="management_fee_separate"
                                                    v-model="form.organisation.management_fee_separate"
                                                    class="focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"
                                                    :class="{'border-red-500': form.errors['organisation.management_fee_separate'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                                    :disabled="this.$page.props.auth.user_role == 3" />
                                            </div>
                                        </div>
                                        <div>
                                            <small class="text-sm text-gray-500">
                                                Only select this box if the organisation manages management fees separately.
                                            </small>
                                        </div>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-2">
                                        <div class="flex items-center">
                                            <label for="primary_for_management_fee_invoices" class="block text-sm font-medium text-gray-700">
                                                Primary For Management Fee Invoices
                                            </label>
                                            <div class="ml-3 flex">
                                                <input type="checkbox"
                                                    id="primary_for_management_fee_invoices"
                                                    v-model="form.organisation.primary_for_management_fee_invoices"
                                                    class="focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"
                                                    :class="{'border-red-500': form.errors['organisation.primary_for_management_fee_invoices'], 'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                                    :disabled="this.$page.props.auth.user_role == 3" />
                                            </div>
                                        </div>
                                        <div>
                                            <small class="text-sm text-gray-500">
                                                Only tick this box if you are to raise management fee invoices with this organisations details on the invoice itself.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                            <button type="submit"
                                :class="{'cursor-not-allowed': this.$page.props.auth.user_role == 3}"
                                :disabled="this.$page.props.auth.user_role == 3"
                                class="bg-pitch-purple border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                <span v-if="updating === true">Update</span>
                                <span v-else>Save</span>
                            </button>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated";
import {computed, ref, reactive} from "vue";
import debounce from "lodash/debounce";
import pickBy from "lodash/pickBy";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";
import {ClipboardCopyIcon, PlusSmIcon, UsersIcon, InformationCircleIcon} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import SupplierTabs from './Components/SupplierTabs.vue';

export default {
    layout: Authenticated,
    components: {
        SupplierTabs,
        ValidationErrors,
        Link,
        Head,
        ClipboardCopyIcon,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon
    },
    setup(props) {

        const organisation = usePage().props.organisation ?? null;
        const updating = usePage().props.updating;

        const form = useForm({
            organisation: {
                id: updating === true ? organisation.id : null,
                name: updating === true ? organisation.name : null,
                short_code: updating === true ? organisation.short_code : null,
                registered_legal_name: updating === true ? organisation.registered_legal_name : null,
                type: organisation.type,
                telephone: updating === true ? organisation.telephone : null,
                mobile: updating === true ? organisation.mobile : null,
                website: updating === true ? organisation.website : null,
                email: updating === true ? organisation.email : null,
                invoice_currency_id: updating === true ? organisation.invoice_currency_id : "",
                commission_vat_type: updating === true ? organisation.commission_vat_type : "net",
                payment_terms: updating === true ? organisation.payment_terms : 14,
                management_fee_separate: updating === true ? (organisation.management_fee_separate == 1 ? true : false) : false,
                primary_for_management_fee_invoices: updating === true ? (organisation.primary_for_management_fee_invoices == 1 ? true : false) : false,
                trading_details_id: updating === true ? organisation.trading_details_id : "",
                cmid: updating === true ? organisation.cmid : null,
                smid: updating === true ? organisation.smid : null,
                ttm_id: updating === true ? organisation.ttm_id : null,
            },
            organisation_address: {
                street: updating === true ? organisation.address.street : null,
                address_line_1: updating === true ? organisation.address.address_line_1 : null,
                address_line_2: updating === true ? organisation.address.address_line_2 : null,
                address_line_3: updating === true ? organisation.address.address_line_3 : null,
                city: updating === true ? organisation.address.city : null,
                state: updating === true ? organisation.address.state : null,
                code: updating === true ? organisation.address.code : null,
                country_id: updating === true ? organisation.address.country_id : "",
            },
            service: {
                name: (updating === true && ("service" in organisation)) ? organisation.service.name : null,
                service_type_id: (updating === true && ("service" in organisation)) ? organisation.service.service_type_id : "",
                supplier_group_id: (updating === true && ("service" in organisation)) ? organisation.service.supplier_group_id : "",
                commission_rate_percent: (updating === true && ("service" in organisation)) ? organisation.service.commission_rate_percent : "",
                telephone: (updating === true && ("service" in organisation)) ? organisation.service.telephone : null,
                mobile: (updating === true && ("service" in organisation)) ? organisation.service.mobile : null,
                email: (updating === true && ("service" in organisation)) ? organisation.service.email : null,
                website: (updating === true && ("service" in organisation)) ? organisation.service.website : null,
                notes: (updating === true && ("service" in organisation)) ? organisation.service.notes : null,
                commission_notes: (updating === true && ("service" in organisation)) ? organisation.service.commission_notes : null
            },
            service_address: {
                street: (updating === true && ("service" in organisation)) ? organisation.service.address.street : null,
                address_line_1: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_1 : null,
                address_line_2: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_2 : null,
                address_line_3: (updating === true && ("service" in organisation)) ? organisation.service.address.address_line_3 : null,
                city: (updating === true && ("service" in organisation)) ? organisation.service.address.city : null,
                state: (updating === true && ("service" in organisation)) ? organisation.service.address.state : null,
                code: (updating === true && ("service" in organisation)) ? organisation.service.address.code : null,
                country_id: (updating === true && ("service" in organisation)) ? organisation.service.address.country_id : ""
            },
            tax: {
                company_name: (updating === true && organisation.tax !== null) ? organisation.tax.company_name : null,
                vat_id: (updating === true && organisation.tax !== null) ? organisation.tax.vat_id : null
            },
            tax_address: {
                street: (updating === true && organisation.tax !== null) ? organisation.tax.address.street : null,
                address_line_1: (updating === true && organisation.tax !== null) ? organisation.tax.address.address_line_1 : null,
                address_line_2: (updating === true && organisation.tax !== null) ? organisation.tax.address.address_line_2 : null,
                address_line_3: (updating === true && organisation.tax !== null) ? organisation.tax.address.address_line_3 : null,
                city: (updating === true && organisation.tax !== null) ? organisation.tax.address.city : null,
                state: (updating === true && organisation.tax !== null) ? organisation.tax.address.state : null,
                code: (updating === true && organisation.tax !== null) ? organisation.tax.address.code : null,
                country_id: (updating === true && organisation.tax !== null) ? organisation.tax.address.country_id : ""
            },
            region_ids: (updating === true && ("service" in organisation)) ? organisation.service.address.regions.map(r => r.id) : [],
        });


        // Organisation Types
        const organisation_types = usePage().props.organisation_types;

        // Organisation Types
        const service_types = usePage().props.service_types;

        // Supplier Groups
        const supplier_groups = usePage().props.supplier_groups;

        // Trading Details
        const trading_details = usePage().props.trading_details;

        // Countries
        const countries = usePage().props.countries;

        // Regions
        const regions = (updating === true && ("service" in organisation)) ? countries.find(country => country.id === form.service_address.country_id).regions : [];

        // Currencies
        const currencies = usePage().props.currencies;

        // Users
        const users = usePage().props.users;

        // have we got a default currency?
        var default_currency = currencies.filter(c => c.is_default);

        // if we do and we're making a new organisation
        if(default_currency.length && !updating) {
            // set to default id
            form.organisation.invoice_currency_id = default_currency[0].id;
        }

        // Show errors
        const showErrors = false;


        /**
         * Copy organisation information to the service.
         */
        function copyToService() {
            this.form.service.name = this.form.organisation.name;
            this.form.service.telephone = this.form.organisation.telephone;
            this.form.service.mobile = this.form.organisation.mobile;
            this.form.service.email = this.form.organisation.email;
            this.form.service.website = this.form.organisation.website;
            this.form.service_address.street = this.form.organisation_address.street;
            this.form.service_address.address_line_1 = this.form.organisation_address.address_line_1;
            this.form.service_address.address_line_2 = this.form.organisation_address.address_line_2;
            this.form.service_address.address_line_3 = this.form.organisation_address.address_line_3;
            this.form.service_address.code = this.form.organisation_address.code;
            this.form.service_address.city = this.form.organisation_address.city;
            this.form.service_address.country_id = this.form.organisation_address.country_id;
        }

        /**
         * Copy organisation information to the service.
         */
        function copyToOrganisation() {
            this.form.organisation.name = this.form.service.name;
            this.form.organisation.telephone = this.form.service.telephone;
            this.form.organisation.mobile = this.form.service.mobile;
            this.form.organisation.email = this.form.service.email;
            this.form.organisation.website = this.form.service.website;
            this.form.organisation_address.street = this.form.service_address.street;
            this.form.organisation_address.address_line_1 = this.form.service_address.address_line_1;
            this.form.organisation_address.address_line_2 = this.form.service_address.address_line_2;
            this.form.organisation_address.address_line_3 = this.form.service_address.address_line_3;
            this.form.organisation_address.code = this.form.service_address.code;
            this.form.organisation_address.city = this.form.service_address.city;
            this.form.organisation_address.country_id = this.form.service_address.country_id;
        }

        /**
         * Copy the organisation information to the tax section.
         */
        function copyToTax() {
            this.form.tax.company_name = this.form.organisation.name;
            this.form.tax_address.street = this.form.organisation_address.street;
            this.form.tax_address.address_line_1 = this.form.organisation_address.address_line_1;
            this.form.tax_address.address_line_2 = this.form.organisation_address.address_line_2;
            this.form.tax_address.address_line_3 = this.form.organisation_address.address_line_3;
            this.form.tax_address.code = this.form.organisation_address.code;
            this.form.tax_address.city = this.form.organisation_address.city;
            this.form.tax_address.state = this.form.organisation_address.state;
            this.form.tax_address.country_id = this.form.organisation_address.country_id;
        }

        /**
         * Copy the service information to the tax section.
         */
        function copyServiceToTax() {
            this.form.tax.company_name = this.form.service.name;
            this.form.tax_address.street = this.form.service_address.street;
            this.form.tax_address.address_line_1 = this.form.service_address.address_line_1;
            this.form.tax_address.address_line_2 = this.form.service_address.address_line_2;
            this.form.tax_address.address_line_3 = this.form.service_address.address_line_3;
            this.form.tax_address.code = this.form.service_address.code;
            this.form.tax_address.city = this.form.service_address.city;
            this.form.tax_address.state = this.form.service_address.state;
            this.form.tax_address.country_id = this.form.service_address.country_id;
        }

        /**
         * Submit the form and perform validation on the request.
         */
        function submit() {
            if (this.updating === true) {

                // Overwrite the region ids, solves an issue with inserting into db.

                this.form.post(route('frontend.organisations.' + this.organisation.type + 's.organisation.update', [organisation.id]), {
                    onError: (page) => {
                        this.showErrors = true
                    },
                    onSuccess: (page) => {
                        this.form.region_ids = region_ids
                    },
                    preserveState: (page) => Object.keys(page.props.errors).length
                });

            } else {
                this.form.post(route('frontend.organisations.store'), {
                    onError: (page) => {
                        this.showErrors = true
                    },
                    preserveState: (page) => Object.keys(page.props.errors).length
                })
            }
        }


        return {
            form,
            organisation_types,
            trading_details,
            countries,
            regions,
            service_types,
            supplier_groups,
            currencies,
            users,
            copyToService,
            copyToOrganisation,
            copyToTax,
            copyServiceToTax,
            submit,
            organisation,
            updating,
            showErrors
        }

    },
    watch: {
        'form.service_address.country_id': {
            deep: true,
            handler(id) {
                if(id === "") {
                    this.regions = [];
                    return;
                }
                if (this.countries.find(country => country.id === id) === "undefined" && typeof this.countries.find(country => country.id === id).regions === "undefined") {
                    this.regions = [];
                } else {
                    if(this.countries.find(country => country.id === id).regions.length > 0) {
                        this.regions = this.countries.find(country => country.id === id).regions

                        // Find NA region
                        const notAvailableRegion = this.regions.find(region => region.name == 'NA');

                        // Set it to default if it exists
                        if (notAvailableRegion) {
                            this.form.region_ids[0] = notAvailableRegion.id;
                        } else {
                            this.form.region_ids = [];
                        }
                    }
                }

            }
        }
    }
}
</script>
